// Angular
import { Injectable, inject, DOCUMENT } from '@angular/core';
// RxJS
import { Observable, ReplaySubject } from 'rxjs';

export type ScriptAppendTarget = 'head' | 'body';

@Injectable({
    providedIn: 'root'
})
export class LazyLoadScriptService {

    private readonly document = inject<Document>(DOCUMENT);

    // Public
    loadedLibraries: Record<string, ReplaySubject<void>> = {};

    /**
     * Load Script
     * @param url
     * @param id
     * @param appendTo
     */
    loadScript(url: string, id?: string, appendTo: ScriptAppendTarget = 'body'): Observable<void> {

        // Already loading/loaded
        if (this.loadedLibraries[url]) {
            return this.loadedLibraries[url].asObservable();
        }

        const existingScript = id ? this.document.getElementById(id) : this.document.querySelector(`script[src="${url}"]`);

        const subject = new ReplaySubject<void>(1);

        this.loadedLibraries[url] = subject;

        // Script already exists in DOM
        if (existingScript) {
            subject.next();
            subject.complete();
            return subject.asObservable();
        }

        const script = this.document.createElement('script');
        script.type = 'text/javascript';

        // Important for execution order
        script.async = false;
        script.defer = true;
        script.src = url;

        if (id) {
            script.id = id;
        }

        script.onload = () => {
            subject.next();
            subject.complete();
        };

        script.onerror = () => {
            script.remove();
            delete this.loadedLibraries[url];
            subject.error(new Error(`Failed to load script: ${url}`));
        };

        const target = appendTo === 'head' ? this.document.head : this.document.body;
        target.appendChild(script);
        return subject.asObservable();
    }
}